import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider } from './context/AuthContext';
import { SiteSettingsProvider } from './context/SiteSettingsContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import PublicLayout from './components/layout/PublicLayout';
import DashboardLayout from './components/layout/DashboardLayout';

// Public pages
import Home from './pages/Home/Home';
import FindSurveyor from './pages/Members/FindSurveyor';
import About from './pages/About/About';
import MissionVision from './pages/MissionVision/MissionVision';
import Constitution from './pages/Constitution/Constitution';
import ExecutiveMembers from './pages/ExecutiveMembers/ExecutiveMembers';
import Subgroup from './pages/Subgroup/Subgroup';
import ScaleOfFees from './pages/ScaleOfFees/ScaleOfFees';
import Gallery from './pages/Gallery/Gallery';
import Contact from './pages/Messages/Contact';
import PublicProfile from './pages/Members/PublicProfile';

// Auth pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';

// Member pages
import MemberDashboard from './pages/Dashboard/MemberDashboard';
import MyProfile from './pages/Members/MyProfile';
import Payments from './pages/Payments/Payments';
import MCPDLog from './pages/MCPD/MCPDLog';
import MemberSettings from './pages/Settings/MemberSettings';

// Admin pages
import AdminDashboard from './pages/Dashboard/AdminDashboard';
import AdminMembers from './pages/Members/AdminMembers';
import AdminFinancials from './pages/Payments/AdminFinancials';
import AdminMCPD from './pages/MCPD/AdminMCPD';
import AdminAnnouncements from './pages/Announcements/AdminAnnouncements';
import AdminReports from './pages/Reports/AdminReports';
import AdminSettings from './pages/Settings/AdminSettings';
import AdminIncomeExpenses from './pages/IncomeExpenses/AdminIncomeExpenses';
import AdminCertificates from './pages/Certificates/AdminCertificates';
import AdminPageEditor from './pages/PageEditor/AdminPageEditor';
import AdminPaymentGateway from './pages/PaymentGateway/AdminPaymentGateway';
import AdminRolesPermissions from './pages/RolesPermissions/AdminRolesPermissions';
import AdminCustomerLedger from './pages/Ledger/AdminCustomerLedger';
import AdminMessages from './pages/Messages/AdminMessages';
import AdminInventory from './pages/Inventory/AdminInventory';
import AdminAGM from './pages/AGM/AdminAGM';
import AdminEvents from './pages/Events/AdminEvents';
import AdminCommittee from './pages/Committee/AdminCommittee';
import AdminMinute from './pages/Minutes/AdminMinute';
import AdminCouncilReport from './pages/CouncilReport/AdminCouncilReport';
import AdminBackup from './pages/Backup/AdminBackup';
import AdminQRCodes from './pages/QRCodes/AdminQRCodes';
import AdminScaleOfFees from './pages/ScaleOfFees/AdminScaleOfFees';
import AdminInvoices from './pages/Invoice/AdminInvoices';
import MyLedger from './pages/Ledger/MyLedger';
import MemberAGM from './pages/AGM/MemberAGM';
import MemberCommittee from './pages/Committee/MemberCommittee';
import MemberMinute from './pages/Minutes/MemberMinute';
import MemberCouncilReport from './pages/CouncilReport/MemberCouncilReport';
import MemberCertificates from './pages/Certificates/MemberCertificates';
import MemberEvents from './pages/Events/MemberEvents';
import MemberMessages from './pages/Messages/MemberMessages';
import MyInvoices from './pages/Invoice/MyInvoices';
import MemberScaleOfFees from './pages/ScaleOfFees/MemberScaleOfFees';
import MyQRCode from './pages/QRCodes/MyQRCode';
import VerifyMember from './pages/Verify/VerifyMember';

function App() {
  return (
    <BrowserRouter>
      <SiteSettingsProvider>
      <AuthProvider>
        <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false}
          newestOnTop closeOnClick pauseOnHover theme="colored" />

        <Routes>
          {/* Public Routes */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/mission-vision" element={<MissionVision />} />
            <Route path="/constitution" element={<Constitution />} />
            <Route path="/executive-members" element={<ExecutiveMembers />} />
            <Route path="/subgroup" element={<Subgroup />} />
            <Route path="/scaleoffees" element={<ScaleOfFees />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/find-surveyor" element={<FindSurveyor />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/member/:slug" element={<PublicProfile />} />
            <Route path="/verify/:slug" element={<VerifyMember />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
          </Route>

          {/* Member Portal Routes */}
          <Route element={<ProtectedRoute allowedRoles={['member', 'admin']} />}>
            <Route element={<DashboardLayout />}>
              <Route path="/portal" element={<MemberDashboard />} />
              <Route path="/portal/dashboard" element={<MemberDashboard />} />
              <Route path="/portal/profile" element={<MyProfile />} />
              <Route path="/portal/payments" element={<Payments />} />
              <Route path="/portal/mcpd" element={<MCPDLog />} />
              <Route path="/portal/settings" element={<MemberSettings />} />
              <Route path="/portal/ledger" element={<MyLedger />} />
              <Route path="/portal/agm" element={<MemberAGM />} />
              <Route path="/portal/committee" element={<MemberCommittee />} />
              <Route path="/portal/minute" element={<MemberMinute />} />
              <Route path="/portal/council-report" element={<MemberCouncilReport />} />
              <Route path="/portal/events" element={<MemberEvents />} />
              <Route path="/portal/certificates" element={<MemberCertificates />} />
              <Route path="/portal/messages" element={<MemberMessages />} />
              <Route path="/portal/scale-of-fees" element={<MemberScaleOfFees />} />
              <Route path="/portal/invoices" element={<MyInvoices />} />
              <Route path="/portal/qr" element={<MyQRCode />} />
            </Route>
          </Route>

          {/* Admin Routes */}
          <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
            <Route element={<DashboardLayout />}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/members" element={<AdminMembers />} />
              <Route path="/admin/financials" element={<AdminFinancials />} />
              <Route path="/admin/mcpd" element={<AdminMCPD />} />
              <Route path="/admin/announcements" element={<AdminAnnouncements />} />
              <Route path="/admin/reports" element={<AdminReports />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
              <Route path="/admin/income-expenses" element={<AdminIncomeExpenses />} />
              <Route path="/admin/certificates" element={<AdminCertificates />} />
              <Route path="/admin/qrcodes" element={<AdminQRCodes />} />
              <Route path="/admin/scale-of-fees" element={<AdminScaleOfFees />} />
              <Route path="/admin/invoices" element={<AdminInvoices />} />
              <Route path="/admin/page-editor" element={<AdminPageEditor />} />
              <Route path="/admin/payment-gateway" element={<AdminPaymentGateway />} />
              <Route path="/admin/roles-permissions" element={<AdminRolesPermissions />} />
              <Route path="/admin/customer-ledger" element={<AdminCustomerLedger />} />
              <Route path="/admin/messages" element={<AdminMessages />} />
              <Route path="/admin/inventory" element={<AdminInventory />} />
              <Route path="/admin/events" element={<AdminEvents />} />
              <Route path="/admin/agm" element={<AdminAGM />} />
              <Route path="/admin/committee" element={<AdminCommittee />} />
              <Route path="/admin/minute" element={<AdminMinute />} />
              <Route path="/admin/council-report" element={<AdminCouncilReport />} />
              <Route path="/admin/backup" element={<AdminBackup />} />
            </Route>
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
      </SiteSettingsProvider>
    </BrowserRouter>
  );
}

export default App;
